import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useSetRecoilState } from 'recoil';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useCopyStructureSet } from '~/hooks/structure-set/useCopyStructureSet';
import { useDetachedContourRun } from '~/hooks/workflows/backend/tasks/useDetachedContourRun';
import {
    ContourAssignment,
    replanningRegisterTabState,
    replanningRunOutputsState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    namesMissingFrom,
    planContourMerge,
    sourceColorsFor,
} from './contourMergePlan';
import { logErrorName } from '~/utils/logErrorName';

const NONE: readonly string[] = [];
const NO_ASSIGNMENTS: readonly ContourAssignment[] = [];

function report(notes: readonly string[], variant: 'warning' | 'error') {
    if (notes.length > 0) {
        enqueueSnackbar(notes.join(' '), { variant, autoHideDuration: null });
    }
}

// Every listed structure is propagated; Contour+ replaces the ones it is assigned and produced.
export function useMergeContouredRois(): void {
    const patient = useSelectedPatient();
    const [runOutputs, setRunOutputs] = useRecoilState(
        replanningRunOutputsState
    );
    const {
        sourceUid,
        pendingPropagatedUid,
        propagatedRoiNames = NONE,
        contourRois = NO_ASSIGNMENTS,
        contourJobId,
    } = runOutputs;
    const setRegisterTab = useSetRecoilState(replanningRegisterTabState);
    const { copyToTarget } = useCopyStructureSet();
    const contourRun = useDetachedContourRun(contourJobId);
    const attempted = useRef<string | undefined>();
    const intl = useIntl();

    useEffect(() => {
        if (
            !patient ||
            !pendingPropagatedUid ||
            attempted.current === pendingPropagatedUid
        ) {
            return;
        }
        const propagated = patient.structureSets.find(
            (set) => set.dicomUid === pendingPropagatedUid
        );
        const targetImage = propagated?.image;
        if (!propagated || !targetImage) {
            return;
        }
        if (contourRois.length > 0 && contourRun.kind === 'pending') {
            return;
        }
        attempted.current = pendingPropagatedUid;

        const say = (id: string, values?: Record<string, string | number>) =>
            intl.formatMessage({ id }, values);
        const listOf = (names: readonly string[]) => names.join(', ');
        const contourNames = contourRois.map((assignment) => assignment.name);

        // Judged on the finished set: exactly the list, anything else named.
        const finish = (
            notes: string[],
            propagatedInstead: readonly string[],
            variant: 'warning' | 'error' = 'warning'
        ) => {
            const held = propagated.roiArray.map((roi) => roi.name);
            const missing = namesMissingFrom(propagatedRoiNames, held);
            const unlisted = namesMissingFrom(held, propagatedRoiNames);
            if (missing.length > 0) {
                notes.push(
                    say('replanning.merge.notPropagated', {
                        names: listOf(missing),
                    })
                );
            }
            if (unlisted.length > 0) {
                notes.push(
                    say('replanning.merge.notListed', {
                        names: listOf(unlisted),
                    })
                );
            }
            report(notes, missing.length > 0 ? 'error' : variant);
            setRegisterTab('visualization');
            setRunOutputs((outputs) =>
                outputs.pendingPropagatedUid === pendingPropagatedUid
                    ? {
                          ...outputs,
                          pendingPropagatedUid: undefined,
                          propagatedUid: pendingPropagatedUid,
                          // Shown on their rows, not in a message.
                          contourFallbackNames: namesMissingFrom(
                              propagatedInstead,
                              missing
                          ),
                      }
                    : outputs
            );
        };

        if (contourRois.length === 0) {
            finish([], []);
            return;
        }
        if (contourRun.kind !== 'ready') {
            const reason =
                contourRun.kind === 'failed'
                    ? [say(contourRun.messageId), contourRun.detail]
                          .filter(Boolean)
                          .join(' ')
                    : say(
                          contourRun.kind === 'cancelled'
                              ? 'replanning.contour.cancelled'
                              : 'replanning.contour.notRun'
                      );
            finish(
                [reason, say('replanning.merge.propagatedInstead')],
                contourNames,
                'error'
            );
            return;
        }

        const plan = planContourMerge(
            contourRois,
            contourRun.structureSet.roiArray
        );
        if (plan.targetNames.size === 0) {
            finish([], plan.missing);
            return;
        }

        // Each Contour+ structure takes the color its structure has in the source set.
        const source = patient.structureSets.find(
            (set) => set.dicomUid === sourceUid
        );
        const targetColors = sourceColorsFor(plan.targetNames, source);
        const notes =
            targetColors.size < plan.targetNames.size
                ? [say('replanning.merge.colorUnknown')]
                : [];

        void (async () => {
            try {
                const result = await copyToTarget(
                    {
                        sourceStructureSet: contourRun.structureSet,
                        targetStructureSet: propagated,
                        targetImage,
                        selectedRoiNumbers: new Set(plan.targetNames.keys()),
                        existingStructureSets: patient.structureSets,
                        // The propagated contour of the same structure gives way to Contour+'s.
                        overwriteExisting: true,
                        targetNames: plan.targetNames,
                        targetColors,
                    },
                    patient
                );
                // Nothing was copied: a set lost edits to a failed save, and the user was told which.
                if (!result) {
                    finish(
                        [...notes, say('replanning.merge.propagatedInstead')],
                        contourNames,
                        'error'
                    );
                    return;
                }
                // A structure Contour+ could not replace keeps its propagated contour.
                const notReplaced = result.skippedRois.flatMap((skipped) => {
                    const name = plan.targetNames.get(skipped.roiNumber);
                    return name ? [name] : [];
                });
                if (result.namingFailures.length > 0) {
                    notes.push(
                        say('replanning.merge.renamed', {
                            names: listOf(
                                result.namingFailures.map(
                                    (failure) => failure.actualName
                                )
                            ),
                        })
                    );
                }
                finish(notes, [...plan.missing, ...notReplaced]);
            } catch (error) {
                logErrorName('Contour+ merge failed:', error);
                finish(
                    [
                        ...notes,
                        say('replanning.merge.copyFailed'),
                        say('replanning.merge.propagatedInstead'),
                    ],
                    contourNames,
                    'error'
                );
            }
        })();
    }, [
        patient,
        sourceUid,
        pendingPropagatedUid,
        propagatedRoiNames,
        contourRois,
        contourRun,
        copyToTarget,
        setRunOutputs,
        setRegisterTab,
        intl,
    ]);
}
